import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import Sidebar from "./Components/Sidebar";
import LoginForm from "./Components/LoginForm";
import EmployeeAllowance from "./Pages/EmployeeAllowance";
import Dashboard from "./Pages/Dashboard";
import Roles from "./Pages/Roles";
import UserRoles from "./Pages/UserRoles";
import Oraganization from "./Pages/Organization";
import Department from "./Pages/Department";
import ClientProjects from "./Pages/ClientProjects";
import EmployeeProjects from "./Pages/EmployeeProjects";
import Timesheet from "./Pages/TimesheetV2";
import TimesheetTracker from "./Pages/TimesheetTracker";
import Payslip from "./Pages/Payslips";
import LeaveRequest from "./Pages/LeaveRequest";
import AssetDetails from "./Pages/AssetDetails";
import EmployeeAssets from "./Pages/EmployeeAssets";
import Clients from "./Pages/Clients";
import EmployeeSalary from "./Pages/EmployeeSalaries";
import Employees from "./Pages/Employees";
import Assets from "./Pages/Assets";
import EmployeeDetails from "./Pages/EmployeeDetails";
import LeaveApproval from "./Pages/LeaveApproval";
import ProtectedRoute from "./utils/ProtectedRoute";
import Unauthorized from "./Pages/Unauthorized";
import EmployeeAttendance from "./Pages/EmployeeAttendance";
import Users from "./Pages/Users";
import Screens from "./Pages/Screens";
import Groups from "./Pages/Groups";
import Permissions from "./Pages/permissions";
import Profile from "./Pages/Profile";
import ExperienceSkills from "./Pages/ExperienceSkills";
import Breadcrumb from "./Components/Breadcrumb";
import Antivirus from "./Pages/Antivirus";
import Holidays from "./Pages/Holidays";
import { FavoritesProvider } from "./utils/FavoritesContext";
const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const App = () => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isLoginPage =
    location.pathname === "/login" || location.pathname === "/";
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = loading
  const [mobileOpen, setMobileOpen] = useState(!isMobile);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const navigate = useNavigate();

  // Check session on mount — try refresh token if access token expired
  useEffect(() => {
    const checkSession = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/me`, {
          credentials: "include",
        });
        if (res.ok) {
          setIsAuthenticated(true);
        } else if (res.status === 401) {
          // Access token expired — try to refresh silently
          try {
            const refreshRes = await fetch(
              `${BASE_URL}/api/auth/refresh-token`,
              {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({}),
              },
            );
            if (refreshRes.ok) {
              // New access_token cookie set — verify session again
              const retryRes = await fetch(`${BASE_URL}/api/me`, {
                credentials: "include",
              });
              setIsAuthenticated(retryRes.ok);
            } else {
              setIsAuthenticated(false);
            }
          } catch {
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }
      } catch {
        setIsAuthenticated(false);
      } finally {
        setIsCheckingSession(false);
      }
    };
    checkSession();
  }, []);
  // Handle sidebar toggle for mobile view
  const handleSidebarToggle = () => setMobileOpen((prev) => !prev);

  if (isCheckingSession) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <div>Loading...</div>
      </Box>
    );
  }

  return (
    <FavoritesProvider>
      <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
        {!isLoginPage && (
          <Sidebar
            mobileOpen={mobileOpen}
            onToggle={handleSidebarToggle}
            isMobile={isMobile}
          />
        )}

        <Box
          component="main"
          sx={{
            flex: 1,
            overflow: "auto",
            mt: "64px",
            ml: !isLoginPage && mobileOpen ? "100px" : 0,
            transition: "margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {!isLoginPage && <Breadcrumb />}
          <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Routes>
              <Route
                path="/login"
                element={
                  isAuthenticated ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <LoginForm />
                  )
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute screen="Dashboard" action="View">
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/roles"
                element={
                  <ProtectedRoute screen="Roles" action="View">
                    <Roles />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/userroles"
                element={
                  <ProtectedRoute screen="User Roles" action="View">
                    <UserRoles />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/department"
                element={
                  <ProtectedRoute screen="Department" action="View">
                    <Department />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/organization"
                element={
                  <ProtectedRoute screen="Organization" action="View">
                    <Oraganization />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/antivirus"
                element={
                  <ProtectedRoute screen="Anti Virus" action="View">
                    <Antivirus />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/clients"
                element={
                  <ProtectedRoute screen="Clients" action="View">
                    <Clients />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/clientprojects"
                element={
                  <ProtectedRoute screen="Client Projects" action="View">
                    <ClientProjects />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/assets"
                element={
                  <ProtectedRoute screen="Assets" action="View">
                    <Assets />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employeeprojects"
                element={
                  <ProtectedRoute screen="Employee Projects" action="View">
                    <EmployeeProjects />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/timesheet"
                element={
                  <ProtectedRoute screen="Timesheet" action="View">
                    <Timesheet />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/timesheettracker"
                element={
                  <ProtectedRoute screen="Timesheet Approval" action="View">
                    <TimesheetTracker />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payslip"
                element={
                  <ProtectedRoute screen="Payslip" action="View">
                    <Payslip />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leaverequest"
                element={
                  <ProtectedRoute screen="Leave Request" action="View">
                    <LeaveRequest />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employeeassets"
                element={
                  <ProtectedRoute screen="Employee Assets" action="View">
                    <EmployeeAssets />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employeesalaries"
                element={
                  <ProtectedRoute screen="Employee Salaries" action="View">
                    <EmployeeSalary />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employeedetails"
                element={
                  <ProtectedRoute screen="Employees" action="View">
                    <EmployeeDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leaveapproval"
                element={
                  <ProtectedRoute screen="Leave Approval" action="View">
                    <LeaveApproval />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/assetdetails"
                element={
                  <ProtectedRoute screen="Assets" action="View">
                    <AssetDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employeeattendance"
                element={
                  <ProtectedRoute screen="Check In Out" action="View">
                    <EmployeeAttendance />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employee-allowance"
                element={
                  <ProtectedRoute screen="Employee Allowance" action="View">
                    <EmployeeAllowance />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users"
                element={
                  <ProtectedRoute screen="Users" action="View">
                    <Users />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/screens"
                element={
                  <ProtectedRoute screen="Screens" action="View">
                    <Screens />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/groups"
                element={
                  <ProtectedRoute screen="Groups" action="View">
                    <Groups />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/permissions"
                element={
                  <ProtectedRoute screen="Permissions" action="View">
                    <Permissions />
                  </ProtectedRoute>
                }
              />
              <Route path="/profile" element={<Profile />} />
              <Route
                path="/experience-skills"
                element={
                  <ProtectedRoute screen="Experience & Skills" action="View">
                    <ExperienceSkills />
                  </ProtectedRoute>
                }
              />
              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route path="/holidays" element={<Holidays />} />
              <Route path="/" element={<Navigate to="/login" replace />} />
            </Routes>
          </Box>
        </Box>
      </Box>
    </FavoritesProvider>
  );
};

const AppWithRouter = () => (
  <Router>
    <App />
  </Router>
);

export default AppWithRouter;
